import { createAction, loadScript, retryPromise } from '@mews/framework';
import { CommanderThunkAction } from '@mews-commander/types';
import { GeneralUser } from '@mews-commander/data-access';
import { PlatformEnvironment } from '@mews/enums';
import { secureIframe } from '@mews-commander/utils';
import { getCurrentEnterpriseId } from '@mews-commander/core';
import { isCurrentProfilePlatformAdmin } from '@mews-commander/legacy-entities/src/selectors/session/scope';
import { getUserBeamerConfiguration, IFRAME_SELECTORS } from '../../session/selectors/3rdParty';
import { getPlatformEnvironment } from '../reducers/configuration';

declare global {
    interface Window {
        beamer_config?: { [K: string]: unknown };
        Beamer?: { [K: string]: unknown; init(): void };
    }
}

const BEAMER_SCRIPT = 'https://app.getbeamer.com/js/beamer-embed.js';

const BEAMER_INITIALIZED = 'BEAMER_INITIALIZED';
const BEAMER_LOADED = 'BEAMER_LOADED';

const initBeamer =
    (user: GeneralUser): CommanderThunkAction =>
    (dispatch, getState) => {
        const config = getUserBeamerConfiguration(getState(), { user });

        window.beamer_config = {
            ...window.beamer_config,
            ...config,
        };

        if (window.Beamer) {
            window.Beamer.init();
        }

        dispatch(createAction(BEAMER_INITIALIZED));
    };

export const loadBeamer =
    (user: GeneralUser): CommanderThunkAction =>
    (dispatch, __, { loggerService }) => {
        const isPlatformAdmin = isCurrentProfilePlatformAdmin();
        const environment = getPlatformEnvironment();
        const enterpriseId = getCurrentEnterpriseId();
        const devOrTestEnvironment =
            environment === PlatformEnvironment.Development || environment === PlatformEnvironment.Test;

        const shouldLoadBeamer = !devOrTestEnvironment && !window.Beamer && !isPlatformAdmin;

        if (shouldLoadBeamer) {
            window.beamer_config = {
                product_id: 'gsyZinOl35397',
                selector: '#beamerElement',
                lazy: true,
                button: false,
            };

            loadScript(BEAMER_SCRIPT)
                .then(() => {
                    dispatch(initBeamer(user));
                    dispatch(createAction(BEAMER_LOADED));
                })
                .catch((error) => {
                    loggerService.log('script-load-error', 'Beamer javascript could not be loaded.', {
                        error,
                        enterpriseId,
                    });
                })
                .then(async () => retryPromise(async () => secureIframe(IFRAME_SELECTORS.beamerUtilities)))
                .catch((error) => {
                    loggerService.log('iframe-error', `Iframe ${error} could not be found`, {
                        error,
                        enterpriseId: getCurrentEnterpriseId(),
                    });
                });
        }
    };
